import React, { Fragment } from 'react';
import { TextMorph } from 'torph/react';
import { Button, cn, Separator } from '@admin-panel/ui';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import { LookDTOType } from '../schemas/styling';

export type LookContentSectionKey = 'occasion' | 'look_name' | 'website' | 'pdp' | 'search_seeds';
type VisibleSectionKey = LookContentSectionKey | 'visual_search';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const getString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
const withFallback = (value: string, fallback = '—') => (value ? value : fallback);
const getNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const buildImageUrl = (value: string, width: number) =>
  value ? updateAttributesOfCdnImage(value, { width, quality: 100 }) || value : '';

type EnrichmentPayload = {
  status?: string;
  built_at?: string | null;
  build_error?: string;
  look_copy?: {
    look_name?: string;
    look_hook?: string;
    styling_blurb?: string;
  };
  widgets: Array<ComponentWidget>;
  widgetSources: Array<WidgetSource>;
  sourceItems: Array<{
    isolatedImageUrl: string;
    mappedLkStyles: Array<string>;
    mappedProductImages: Record<string, string>;
    productType: string;
  }>;
};

type ComponentMatch = {
  id: string;
  name: string;
  assetUrl: string;
  deepLink: string;
  rank: number | null;
  lkStyleNumber: string;
};

type WidgetSource = {
  itemKey: string;
  componentGroup: string;
  widgetId: string;
  productLabel: string;
  isolatedImageUrl: string;
  matches: Array<ComponentMatch>;
};

type ComponentWidget = {
  id: string;
  title: string;
  description: string;
  products: Array<ComponentMatch>;
};

const Section = ({
  title,
  children,
  onEdit,
  canEdit = false,
}: {
  title: string;
  children: React.ReactNode;
  onEdit?: () => void;
  canEdit?: boolean;
}) => (
  <section className="rounded-lg border border-border/80 bg-background px-3 py-2.5">
    <header className="mb-2 flex items-center justify-between gap-2">
      <TextMorph
        as="h3"
        duration={260}
        ease="cubic-bezier(0.19, 1, 0.22, 1)"
        className="m-0 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase"
      >
        {title}
      </TextMorph>
      {canEdit && onEdit ? (
        <Button size="small" variant="outline" type="button" onClick={onEdit}>
          Edit
        </Button>
      ) : null}
    </header>
    <div>{children}</div>
  </section>
);

const LabelRow = ({ label, value }: { label: string; value?: string }) => (
  <dl className="grid grid-cols-[auto_1fr] items-start gap-x-2 text-sm">
    <dt className="font-medium text-foreground">{label}:</dt>
    <dd className="text-muted-foreground">{withFallback(value || '')}</dd>
  </dl>
);

const TextBlock = ({ label, value }: { label: string; value?: string }) => (
  <article className="prose prose-sm max-w-none prose-p:my-1 prose-headings:my-0 prose-headings:text-xs prose-headings:font-semibold prose-headings:tracking-wide prose-headings:text-foreground prose-headings:uppercase">
    <h4>{label}</h4>
    <p className="whitespace-pre-wrap text-muted-foreground">{withFallback(value || '')}</p>
  </article>
);

type PdpProductRecommendation = {
  product_type: string;
  description: string;
  mapped_lk_styles?: Array<string>;
  mapped_product_images?: Record<string, unknown>;
};

const normalizeEnrichmentPayload = (value: unknown): EnrichmentPayload => {
  if (Array.isArray(value)) {
    return {
      widgets: [],
      widgetSources: [],
      sourceItems: value
        .map(item =>
          isRecord(item)
            ? {
                isolatedImageUrl: getString(item.isolated_image_url),
                mappedLkStyles: Array.isArray(item.mapped_lk_styles)
                  ? item.mapped_lk_styles.map(entry => getString(entry)).filter(Boolean)
                  : [],
                mappedProductImages:
                  item.mapped_product_images &&
                  typeof item.mapped_product_images === 'object' &&
                  !Array.isArray(item.mapped_product_images)
                    ? Object.fromEntries(
                        Object.entries(item.mapped_product_images).filter(
                          ([key, mappedValue]) =>
                            key &&
                            typeof mappedValue === 'string' &&
                            Boolean(mappedValue.trim()),
                        ),
                      )
                    : {},
                productType: getString(item.product_type),
              }
            : null,
        )
        .filter(Boolean) as Array<{
          isolatedImageUrl: string;
          mappedLkStyles: Array<string>;
          mappedProductImages: Record<string, string>;
          productType: string;
        }>,
    };
  }

  if (!isRecord(value)) {
    return {
      widgets: [],
      widgetSources: [],
      sourceItems: [],
    };
  }

  return {
    status: getString(value.status),
    built_at: typeof value.built_at === 'string' ? value.built_at : null,
    build_error: getString(value.build_error),
    look_copy: isRecord(value.look_copy)
      ? {
          look_name: getString(value.look_copy.look_name),
          look_hook: getString(value.look_copy.look_hook),
          styling_blurb: getString(value.look_copy.styling_blurb),
        }
      : undefined,
    widgets: Array.isArray(value.items)
      ? (value.items.map(normalizeComponentWidget).filter(Boolean) as Array<ComponentWidget>)
      : Array.isArray(value.widgets)
      ? (value.widgets.map(normalizeComponentWidget).filter(Boolean) as Array<ComponentWidget>)
      : [],
    widgetSources: Array.isArray(value.widget_sources)
      ? (value.widget_sources.map(normalizeWidgetSource).filter(Boolean) as Array<WidgetSource>)
      : [],
    sourceItems: Array.isArray(value.source_items)
      ? (value.source_items
          .map(item =>
            isRecord(item)
              ? {
                  isolatedImageUrl: getString(item.isolated_image_url),
                  mappedLkStyles: Array.isArray(item.mapped_lk_styles)
                    ? item.mapped_lk_styles.map(entry => getString(entry)).filter(Boolean)
                    : [],
                  mappedProductImages:
                    item.mapped_product_images &&
                    typeof item.mapped_product_images === 'object' &&
                    !Array.isArray(item.mapped_product_images)
                      ? Object.fromEntries(
                          Object.entries(item.mapped_product_images).filter(
                            ([key, mappedValue]) =>
                              key &&
                              typeof mappedValue === 'string' &&
                              Boolean(mappedValue.trim()),
                          ),
                        )
                      : {},
                  productType: getString(item.product_type),
                }
              : null,
          )
          .filter(Boolean) as Array<{
            isolatedImageUrl: string;
            mappedLkStyles: Array<string>;
            mappedProductImages: Record<string, string>;
            productType: string;
          }>)
      : [],
  };
};

const formatStatusLabel = (status?: string) => {
  const value = getString(status);
  if (!value) {
    return 'Not built';
  }
  return value.replace(/_/g, ' ');
};

const formatBuiltAt = (builtAt?: string | null) => {
  if (!builtAt) {
    return '—';
  }

  const parsed = new Date(builtAt);
  if (Number.isNaN(parsed.getTime())) {
    return builtAt;
  }

  return parsed.toLocaleString();
};

const statusToneClassName = (status?: string) => {
  switch (getString(status)) {
    case 'ready':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'building':
    case 'pending':
      return 'border-sky-200 bg-sky-50 text-sky-700';
    case 'pending_input':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'failed':
      return 'border-rose-200 bg-rose-50 text-rose-700';
    default:
      return 'border-border bg-muted/50 text-muted-foreground';
  }
};

const normalizeComponentMatch = (value: unknown): ComponentMatch | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    id: getString(value.id) || getString(value.catalog_product_id),
    name: getString(value.name),
    assetUrl: getString(value.asset_url ?? value.assetUrl),
    deepLink: getString(value.deep_link ?? value.deepLink),
    rank: getNumber(value.rank),
    lkStyleNumber: getString(value.lk_style_number ?? value.lkStyleNumber),
  };
};

const normalizeComponentWidget = (value: unknown): ComponentWidget | null => {
  if (!isRecord(value)) {
    return null;
  }

  const data = isRecord(value.data) ? value.data : {};

  return {
    id: getString(value.id),
    title: getString(data.title),
    description: getString(data.description),
    products: Array.isArray(data.products)
      ? (data.products.map(normalizeComponentMatch).filter(Boolean) as Array<ComponentMatch>)
      : [],
  };
};

const normalizeWidgetSource = (value: unknown): WidgetSource | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    itemKey: getString(value.item_key),
    componentGroup: getString(value.component_group),
    widgetId: getString(value.widget_id),
    productLabel: getString(value.product_label),
    isolatedImageUrl: getString(value.isolated_image_url),
    matches: Array.isArray(value.matches)
      ? (value.matches.map(normalizeComponentMatch).filter(Boolean) as Array<ComponentMatch>)
      : [],
  };
};

const isBaseProductWidget = (widget: ComponentWidget) =>
  widget.id.includes('base_product') || widget.title === 'Base Product';

const widgetSourcesForWidget = (widget: ComponentWidget, sources: Array<WidgetSource>) =>
  sources.filter(source => {
    if (source.widgetId && source.widgetId === widget.id) {
      return true;
    }
    if (source.widgetId && widget.id.endsWith(`_${source.widgetId}`)) {
      return true;
    }
    return false;
  });

const ProductThumb = ({
  imageUrl,
  alt,
  fallback,
}: {
  imageUrl: string;
  alt: string;
  fallback: string;
}) => (
  <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/80 bg-white">
    {imageUrl ? (
      <img src={imageUrl} alt={alt} className="h-full w-full object-cover" />
    ) : (
      <span className="px-1 text-center text-[10px] font-medium text-muted-foreground">
        {fallback}
      </span>
    )}
  </div>
);

const VisualSearchWidgetList = ({
  widgets,
  widgetSources,
}: {
  widgets: Array<ComponentWidget>;
  widgetSources: Array<WidgetSource>;
}) => {
  if (widgets.length === 0) {
    return <p className="text-sm text-muted-foreground">No final widgets built yet.</p>;
  }

  return (
    <div className="space-y-3">
      {widgets.map(widget => {
        const sources = widgetSourcesForWidget(widget, widgetSources);
        return (
          <div
            key={widget.id || widget.title}
            className="rounded-md border border-border/80 bg-muted/20 p-2.5"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="m-0 text-sm font-semibold text-foreground">
                  {withFallback(widget.title)}
                </p>
                <p className="m-0 mt-1 text-xs text-muted-foreground">
                  {withFallback(widget.description)}
                </p>
              </div>
            </div>

            <div className="mt-2 rounded-md border border-border/70 bg-background p-2">
              <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Source Groups
              </p>
              {sources.length > 0 ? (
                <div className="mt-1.5 space-y-2">
                  {sources.map(source => (
                    <div
                      key={`${widget.id}-${source.itemKey}`}
                      className="rounded-md border border-border/80 bg-muted/10 p-2"
                    >
                      <div className="grid gap-3 md:grid-cols-[120px_1fr]">
                        <div className="flex justify-center">
                          <ProductThumb
                            imageUrl={buildImageUrl(source.isolatedImageUrl, 240)}
                            alt={source.productLabel || source.itemKey}
                            fallback="No image"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <p className="m-0 text-sm font-medium text-foreground">
                            {withFallback(source.productLabel)}
                          </p>
                          <p className="m-0 text-xs text-muted-foreground">
                            Item Key: {withFallback(source.itemKey)}
                          </p>
                        </div>
                      </div>

                      {source.matches.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {source.matches.slice(0, 6).map(match => (
                            <div
                              key={`${source.itemKey}-${match.id}-${match.rank ?? 0}`}
                              className="w-[92px] rounded-md border border-border/80 bg-background p-1.5"
                            >
                              <ProductThumb
                                imageUrl={buildImageUrl(match.assetUrl, 160)}
                                alt={match.name || match.id}
                                fallback={match.id || 'No image'}
                              />
                              <p className="mt-1 line-clamp-2 text-[11px] font-medium text-foreground">
                                {withFallback(match.name)}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-muted-foreground">
                          No matched products for this source image yet.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  No isolated image URL available for this widget yet.
                </p>
              )}
            </div>

            {sources.length === 0 && widget.products.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {widget.products.slice(0, 6).map(product => (
                  <div
                    key={`${widget.id}-${product.id}-${product.rank ?? 0}`}
                    className="w-[92px] rounded-md border border-border/80 bg-background p-1.5"
                  >
                    <ProductThumb
                      imageUrl={buildImageUrl(product.assetUrl, 160)}
                      alt={product.name || product.id}
                      fallback={product.id || 'No image'}
                    />
                    <p className="mt-1 line-clamp-2 text-[11px] font-medium text-foreground">
                      {withFallback(product.name)}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const ProductSuggestionList = ({
  recommendations,
}: {
  // title: string;
  recommendations: Array<PdpProductRecommendation>;
}) => (
  <section className="space-y-3">
    {recommendations.length === 0 ? (
      <p className="text-sm text-muted-foreground">No data available</p>
    ) : (
      <ul className="m-0 list-none p-0 [&>li+li]:mt-3">
        {recommendations.map((recommendation, recommendationIndex) => {
          const mappedIds = Array.from(
            new Set((recommendation.mapped_lk_styles || []).map(value => String(value))),
          );
          const mappedImages =
            recommendation.mapped_product_images &&
            typeof recommendation.mapped_product_images === 'object'
              ? recommendation.mapped_product_images
              : {};
          return (
            <Fragment key={`${recommendation.product_type}-${recommendationIndex}`}>
              <li className="m-0 ">
                <p className="m-0 mt-1 whitespace-pre-wrap text-muted-foreground">
                  <span className="m-0 text-xs font-semibold text-foreground capitalize">
                    {recommendation.product_type.replace(/_/g, ' ')}:{' '}
                  </span>
                  {recommendation.description}
                </p>

                {mappedIds.length > 0 ? (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {mappedIds.map((productId, previewIndex) => {
                      const imageCandidate = mappedImages[productId];
                      const imageUrl =
                        typeof imageCandidate === 'string'
                          ? updateAttributesOfCdnImage(imageCandidate, {
                              width: 160,
                              quality: 100,
                            }) || imageCandidate
                          : '';

                      return (
                        <div
                          key={`${recommendationIndex}-${productId}-${previewIndex}`}
                          className="h-14 w-14 overflow-hidden rounded-md bg-white shadow-xs"
                          title={productId}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={productId}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center px-1 text-[10px] font-medium text-muted-foreground">
                              {productId.slice(0, 8)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </li>

              {recommendationIndex !== recommendations.length - 1 && (
                <Separator variant="dashed" className="my-2.5" />
              )}
            </Fragment>
          );
        })}
      </ul>
    )}
  </section>
);

const buildSharedLookSections = (look: LookDTOType) => {
  const metadata = isRecord(look.metadata) ? look.metadata : {};
  const websiteContent = isRecord(metadata.website_content) ? metadata.website_content : {};

  const lookName = getString(look.look_name) || getString(look.base_product_name);

  const websiteEntries = [
    {
      key: 'look_hook',
      label: 'Look Hook',
      value: getString(look.look_hook) || getString(websiteContent.look_hook) || '',
    },
    {
      key: 'stylist_note',
      label: 'Stylist Note',
      value: getString(websiteContent.stylist_note) || '',
    },
    {
      key: 'blurb',
      label: 'Blurb',
      value: getString(look.blurb) || '',
    },
  ];

  const pdpRecommendations = (look.product_recommendations || []).map(item => ({
    product_type: getString(item.product_type),
    description: getString(item.description),
    mapped_lk_styles: (item.mapped_lk_styles || []).map(value => String(value)),
    mapped_product_images:
      item.mapped_product_images && typeof item.mapped_product_images === 'object'
        ? item.mapped_product_images
        : {},
  }));

  return {
    lookName,
    websiteEntries,
    pdpRecommendations,
  };
};

const SharedLookContent = ({
  look,
  onEditSection,
  canEdit = false,
  canEditOccasion = true,
  canTriggerVisualSearch = false,
  isTriggeringVisualSearch = false,
  onTriggerVisualSearch,
  visibleSections,
}: {
  look: LookDTOType;
  onEditSection?: (section: LookContentSectionKey) => void;
  canEdit?: boolean;
  canEditOccasion?: boolean;
  canTriggerVisualSearch?: boolean;
  isTriggeringVisualSearch?: boolean;
  onTriggerVisualSearch?: () => void;
  visibleSections?: Partial<Record<VisibleSectionKey, boolean>>;
}) => {
  const { lookName, websiteEntries, pdpRecommendations } = buildSharedLookSections(look);
  const canEditLookName = canEdit;
  const occasionLine = [look.occasion_name, look.sub_occasion_name ?? '']
    .map(value => getString(value))
    .filter(Boolean)
    .join(' • ');
  const visualSearch = normalizeEnrichmentPayload(look.visual_search);
  const widgetsPayload = normalizeEnrichmentPayload(look.widgets);
  const renderedWidgets = widgetsPayload.widgets.filter(widget => !isBaseProductWidget(widget));
  const hasGeneratedSourceImages = visualSearch.sourceItems.some(item => Boolean(item.isolatedImageUrl));
  const showSection = (section: VisibleSectionKey) => visibleSections?.[section] ?? true;

  return (
    <>
      {showSection('occasion') ? (
        <Section
          title="Occasion"
          canEdit={canEdit && canEditOccasion}
          onEdit={onEditSection ? () => onEditSection('occasion') : undefined}
        >
          <LabelRow label="Occasion • Sub Occasion" value={occasionLine} />
        </Section>
      ) : null}

      {showSection('look_name') ? (
        <Section
          title="Look Name"
          canEdit={canEditLookName}
          onEdit={onEditSection ? () => onEditSection('look_name') : undefined}
        >
          <LabelRow label="Look Name" value={lookName} />
        </Section>
      ) : null}

      {showSection('website') ? (
        <Section
          title="Website Content"
          canEdit={canEdit}
          onEdit={onEditSection ? () => onEditSection('website') : undefined}
        >
          <div className="space-y-3">
            {websiteEntries.map(entry => (
              <TextBlock key={entry.key} label={entry.label} value={entry.value} />
            ))}
          </div>
        </Section>
      ) : null}

      {showSection('pdp') ? (
        <Section
          title="Product Recommendations"
          canEdit={canEdit}
          onEdit={onEditSection ? () => onEditSection('pdp') : undefined}
        >
          <ProductSuggestionList recommendations={pdpRecommendations} />
        </Section>
      ) : null}
      {showSection('search_seeds') &&
      canEdit &&
      onEditSection ? (
        <Section title="Search Seeds" canEdit={true} onEdit={() => onEditSection('search_seeds')}>
          <p className={cn('text-sm text-muted-foreground')}>
            Edit search seed items, queries, and filters.
          </p>
        </Section>
      ) : null}

      {showSection('visual_search') ? (
        <Section title="Visual Search" canEdit={false} onEdit={undefined}>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                  statusToneClassName(widgetsPayload.status),
                )}
              >
                {formatStatusLabel(widgetsPayload.status)}
              </span>
              <span className="text-xs text-muted-foreground">
                Built: {formatBuiltAt(widgetsPayload.built_at)}
              </span>
            </div>

            {widgetsPayload.build_error ? (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-sm text-rose-700">
                {widgetsPayload.build_error}
              </p>
            ) : hasGeneratedSourceImages ? (
              <p className="text-sm text-muted-foreground">
                Source images have already been generated. You can keep searching and saving
                products from the generated source images.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Trigger the component visual search pipeline to extract styled items, isolate them,
                and fetch similar catalog products.
              </p>
            )}

            {canTriggerVisualSearch && onTriggerVisualSearch && !hasGeneratedSourceImages ? (
              <div>
                <Button
                  size="small"
                  variant="outline"
                  type="button"
                  isLoading={isTriggeringVisualSearch}
                  onClick={onTriggerVisualSearch}
                >
                  Trigger Visual Search
                </Button>
              </div>
            ) : hasGeneratedSourceImages ? (
              <div>
                <span className="inline-flex items-center rounded-sm border border-border/70 bg-background px-2.5 py-1 text-xs font-medium text-foreground">
                  Source images generated
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Publish the look to trigger visual search manually from here.
              </p>
            )}

            <div className="rounded-md border border-border/80 bg-muted/10 p-2.5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rendered Widgets
              </p>
              <VisualSearchWidgetList
                widgets={renderedWidgets}
                widgetSources={[]}
              />
            </div>
          </div>
        </Section>
      ) : null}
    </>
  );
};

export const StandardPdpContent = ({
  look,
  onEditSection,
  canEdit,
  canTriggerVisualSearch,
  isTriggeringVisualSearch,
  onTriggerVisualSearch,
  visibleSections,
}: {
  look: LookDTOType;
  onEditSection?: (section: LookContentSectionKey) => void;
  canEdit?: boolean;
  canTriggerVisualSearch?: boolean;
  isTriggeringVisualSearch?: boolean;
  onTriggerVisualSearch?: () => void;
  visibleSections?: Partial<Record<VisibleSectionKey, boolean>>;
}) => (
  <SharedLookContent
    look={look}
    onEditSection={onEditSection}
    canEdit={canEdit}
    canTriggerVisualSearch={canTriggerVisualSearch}
    isTriggeringVisualSearch={isTriggeringVisualSearch}
    onTriggerVisualSearch={onTriggerVisualSearch}
    visibleSections={visibleSections}
  />
);

export const OccasionPdpContent = ({
  look,
  onEditSection,
  canEdit,
  canEditOccasion,
  canTriggerVisualSearch,
  isTriggeringVisualSearch,
  onTriggerVisualSearch,
  visibleSections,
}: {
  look: LookDTOType;
  onEditSection?: (section: LookContentSectionKey) => void;
  canEdit?: boolean;
  canEditOccasion?: boolean;
  canTriggerVisualSearch?: boolean;
  isTriggeringVisualSearch?: boolean;
  onTriggerVisualSearch?: () => void;
  visibleSections?: Partial<Record<VisibleSectionKey, boolean>>;
}) => {
  return (
    <SharedLookContent
      look={look}
      onEditSection={onEditSection}
      canEdit={canEdit}
      canEditOccasion={canEditOccasion}
      canTriggerVisualSearch={canTriggerVisualSearch}
      isTriggeringVisualSearch={isTriggeringVisualSearch}
      onTriggerVisualSearch={onTriggerVisualSearch}
      visibleSections={visibleSections}
    />
  );
};
